import React, { useState, useEffect, useRef } from 'react';
//*MUI
import { Box, Divider } from '@mui/material';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

//*Redux
import { useDispatch, useSelector } from 'react-redux';
import action from '../../Redux Store/actions';

//*files
import cssClasses from './SiteVisit.module.css';
// Reuse the Leads page's filter styles verbatim so the "Filter by" dropdown is identical.
import filterCss from '../Leads/Leads.module.css';
import checked from '../../Assets/Images/Common/checked.svg';
import Button from '../../Subcomponents/Button';
import ViewSiteVisitDetails from '../../Modals/ViewSiteVisitDetails/ViewSiteVisitDetails';
import NoSiteVisitIcon from '../../Assets/Images/Common/NoSiteVisitsFound.svg';
import SearchBar from '../../Subcomponents/Search';
import DateComponent from '../../Subcomponents/DateComponent';
import { debounce, orderBy } from 'lodash';
import { DataGrid } from '@mui/x-data-grid';
import { supabase } from '../../supabaseClient';
import ProjectIcon from '../../Assets/Images/Sustenance/ProjectIcon.svg';
import {
  getAllSiteVisitsByHierarchy,
  updateSiteVisitVerification,
  updateSiteVisitRejection,
  getPendingApprovers,
  getUsersNameMap,
} from '../../Services/SiteVisitMain';
import { getAllCpForLaunch } from '../../Services/ChannelPartner';
import { getUsers, getProjectsForLeadFilter, addLeadLogs } from '../../Services/Lead';
import { successTost, errorTost } from '../../Services/Toast';

// Styling copied verbatim from the Sustenance Site Visit page to keep the UI identical.
const dataGridStyles = {
  '&.MuiDataGrid-root .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  border: '1px solid #EAECF0 !important',
  fontWeight: '400',
  fontSize: '1.4rem',
  lineHeight: '2rem',
  color: '#667085',
  fontFamily: 'Inter-Medium',
  borderRadius: '8px',
  '& div[data-colindex="1"]': {
    color: '#101828',
    fontWeight: '500',
  },
  '& .MuiDataGrid-virtualScrollerRenderZone': {
    '& .MuiDataGrid-row': {
      borderBottom: '1px solid #EAECF0',
      background: '#FFFFFF',
      borderRadius: '1rem',
    },
    '& .MuiDataGrid-row.Mui-selected:hover': {
      borderBottom: '1px solid #EAECF0',
      backgroundColor: '#FFFFFF',
    },
  },
  '& .MuiDataGrid-iconSeparator ': {
    display: 'none',
  },
  '& .MuiDataGrid-columnHeader:hover': {
    '& .MuiDataGrid-menuIcon': {
      visibility: 'hidden',
    },
  },
  '&  .MuiDataGrid-columnHeaders': {
    background: '#F9FAFB',
    border: '1px solid #EAECF0',
    marginBottom: '1rem',
    borderBottom: '6px solid #EAECF0',
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none',
  },
  '& .MuiDataGrid-checkboxInput': {
    '& svg': {
      width: '2rem',
      height: '2rem',
      background: '#FFFFFF',
    },
  },
  '& .MuiDataGrid-footerContainer': {
    '& .MuiTablePagination-displayedRows ': {
      display: 'none',
    },
    '& .MuiDataGrid-selectedRowCount': {
      visibility: 'hidden ',
    },
  },
  "&  [title='Sort']": {
    display: 'none',
    cursor: 'none',
  },
  "& [title='Go to previous page']": {
    border: '1px solid #EAECF0',
    borderRadius: '0.8rem',
    marginRight: '0.5rem',
  },
  "& [title='Go to next page']": {
    border: '1px solid #EAECF0',
    borderRadius: '0.8rem',
    marginRight: '0.5rem',
  },
  "& [title='Go to previous page']:after": {
    content: "'Previous'",
    marginLeft: '1rem',
  },
  "& [title='Go to next page']:before": {
    content: "'Next'",
    marginRight: '1rem',
  },
  '&  .MuiDataGrid-row ': {
    maxHeight: '72px !important',
    minHeight: '72px !important',
  },
  '& .MuiButtonBase-root': {
    fontSize: '1.6rem',
  },
  '& .MuiDataGrid-cell:focus-within': {
    outline: 'none !important',
  },
  '& .MuiDataGrid-cell': {
    padding: '0 2rem',
    minHeight: '72px !important',
    maxHeight: '72px !important',
    '&:focus': {
      outline: 'none',
    },
  },
  '& .MuiDataGrid-columnHeader': {
    padding: '0 1rem',
    '&:focus': {
      outline: 'none',
    },
  },
};

function SiteVisit() {
  const loggedInUser = useSelector((state) => state.Auth.user);
  const dispatch = useDispatch();

  // Super Admin = top of the hierarchy (role level 1).
  const isSuperAdmin = loggedInUser?.roleId?.level === 1;

  // Whether this user may verify/approve site visits. Visibility is already scoped
  // to the user's subtree, so anyone who can see a visit sits at-or-above the lead's
  // mapped parent and is allowed to approve it (per the "userParentId or above" rule).
  const canApprove =
    loggedInUser?.roleId?.accessibleTabs?.['Site Visit']?.Approve?.isAccessible ===
    true;

  // Access level for Site Visits, configured per-role in the Hierarchy editor:
  //   'All'       -> see every CP site visit (no hierarchy restriction)
  //   'Team Wise' -> restricted to the user's hierarchy (Leads.userParentId in their subtree)
  const showMode =
    loggedInUser?.roleId?.accessibleTabs?.['Site Visit']?.['Show Site Visits']?.show;
  const seeAll = showMode === 'All';

  // The logged-in user's hierarchy (self + all descendants), computed at login.
  // Used to decide who may approve: a user may approve a visit when the lead's
  // userParentId is the user themselves OR anyone below them (i.e. userParentId is
  // in their subtree). The claiming rep sits below userParentId, so they're excluded.
  const teamIds = (() => {
    try {
      return JSON.parse(localStorage.getItem('teamIds')) || [];
    } catch (e) {
      return [];
    }
  })();

  const [AllSitesData, setAllSitesData] = useState([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize] = useState(12);
  const [search, setSearch] = useState('');
  const [pageMaintain, setPageMaintain] = useState(0);
  const [noSiteVisitPresent, setNoSiteVisitPresent] = useState(null);
  const [ViewSiteData, setViewSiteData] = useState('');
  const [TotalSiteVisits, setTotalSiteVisits] = useState(0);

  // Always holds a refetch using the CURRENT filters/search/scope. The real-time
  // SiteVisits subscription calls this so its refresh preserves applied filters
  // (its own callback closure is created once and would otherwise be stale).
  const refetchRef = useRef(null);

  // The RPC's `userids` argument that scopes which site visits load:
  //   null      -> no hierarchy filter (All mode / Super Admin sees everything)
  //   []        -> nothing in scope (don't fetch)
  //   [ids...]  -> restricted to leads whose userParentId is in this subtree
  //   undefined -> not resolved yet
  const [userIds, setUserIds] = useState(undefined);

  // { [userId]: name } — used to show the verifier and the designated approver by name.
  const [userNameMap, setUserNameMap] = useState({});

  // Fixed default sort (Latest First); the page has no Sort dropdown.
  const drpDownSelectedVal = 'Latest First';

  // ---- "Filter by" multi-select (Date + CP + SM + Project + Lead Quality + Status),
  // inlined from the Leads tab ----
  // Selected ids/dates/values that scope the fetch (passed to the hierarchy RPC service).
  const [selectedCp, setSelectedCp] = useState([]);
  const [selectedSales, setSelectedSales] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedLeadQuality, setSelectedLeadQuality] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  // Verification filter stores siteVisitVerificationStatus values ('approved' | 'rejected' | 'pending').
  const [selectedVerification, setSelectedVerification] = useState([]);
  const [selectedLeadType, setSelectedLeadType] = useState([]);
  // "Verification Pending With" stores approver userIds (userParentId of pending visits).
  const [selectedPendingWith, setSelectedPendingWith] = useState([]);
  const [selectDates, setSelectDates] = useState([
    { label: 'StartDate', value: '' },
    { label: 'EndDate', value: '' },
  ]);
  const [selectedDates, setSelectedDate] = useState(null);

  // Dropdown UI state.
  const [showDropDown, setShowDropDown] = useState(false);
  const [showDates, setShowDates] = useState(false);
  const [showAllCpDropDown, setShowAllCpDropDown] = useState(false);
  const [showAllSalesDropDown, setShowAllSalesDropDown] = useState(false);
  const [showAllProjectsDropDown, setShowAllProjectsDropDown] = useState(false);
  const [showAllLeadQualityDropDown, setShowAllLeadQualityDropDown] = useState(false);
  const [showAllStatusDropDown, setShowAllStatusDropDown] = useState(false);
  const [showAllVerificationDropDown, setShowAllVerificationDropDown] = useState(false);
  const [showAllLeadTypeDropDown, setShowAllLeadTypeDropDown] = useState(false);
  const [showAllPendingWithDropDown, setShowAllPendingWithDropDown] = useState(false);
  const [allPendingWithDropDown, setAllPendingWithDropDown] = useState([]);
  // Client-side search text for the "Verification Pending With" list.
  const [DropdownPendingWithSearch, setDropdownPendingWithSearch] = useState('');
  const [allCpDropDown, setAllCpDropDown] = useState([]);
  const [allSalesDropDown, setAllSalesDropDown] = useState([]);
  const [allProjectsDropDown, setAllProjectsDropDown] = useState([]);
  // Lead Quality + Status are fixed option lists (mirrors the Leads tab).
  const [allLeadQualityDropDown, setAllLeadQualityDropDown] = useState([
    { label: 'WARM', value: false },
    { label: 'HOT', value: false },
    { label: 'COLD', value: false },
    { label: 'LOST', value: false },
  ]);
  const [allStatusDropDown, setAllStatusDropDown] = useState([
    { label: 'Site Visit Scheduled', value: false },
    { label: 'Site Visit Done', value: false },
    { label: 'Token Payment Pending', value: false },
    { label: 'Token Payment Complete', value: false },
    { label: 'Token Partial Payment Done', value: false },
    { label: 'Checkpoint One Cleared', value: false },
    { label: 'Checkpoint Two Cleared', value: false },
    { label: 'Allotment Payment Pending', value: false },
    { label: 'Allotment Payment Complete', value: false },
    { label: 'Allotment Partial Payment Done', value: false },
    { label: 'Unit On Hold', value: false },
    { label: 'Closed', value: false },
  ]);
  const [allVerificationDropDown, setAllVerificationDropDown] = useState([
    { label: 'Verified', value: false },
    { label: 'Rejected', value: false },
    { label: 'Verification Pending', value: false },
  ]);
  const [allLeadTypeDropDown, setAllLeadTypeDropDown] = useState([
    { label: 'Direct', value: false },
    { label: 'CP', value: false },
    { label: 'R&L', value: false },
  ]);
  const [helperText, setHelperText] = useState({ startDate: '', endDate: '' });
  const [DropDownCpSearch, setDropDownCpSearch] = useState('');
  const [DropdownSalesSearch, setDropDownSalesSearch] = useState('');
  const [DropdownProjectSearch, setDropDownProjectSearch] = useState('');

  // Categories shown in the left list of the dropdown.
  // Categories shown in the "Filter by" dropdown.
  // NOTE: 'Lead Quality' and 'Status' are temporarily commented out (hidden from the
  // dropdown) per request — their handlers/render blocks remain below, inactive, so
  // they can be re-enabled by simply adding them back to this list.
  const filterCategories = [
    'None',
    'Date',
    'CP',
    'SM',
    'Project',
    // 'Lead Quality',
    // 'Status',
    'Verification Status',
    'Lead Type',
    'Verification Pending With',
  ];

  //*View modal
  const [openViewDetails, setOpenViewDetails] = useState(false);
  const handleViewDetailsOpen = () => setOpenViewDetails(true);
  const handleViewDetailsClose = () => setOpenViewDetails(false);

  // Initial scope (before any Sales Manager is picked):
  //  - Super Admin / root, or any role set to 'All' -> null (see everything)
  //  - Team-wise roles -> their own subtree (teamIds from login)
  function resolveInitialScope() {
    if (isSuperAdmin || seeAll) return null;
    try {
      return JSON.parse(localStorage.getItem('teamIds')) || [];
    } catch (e) {
      return [];
    }
  }

  useEffect(() => {
    dispatch(action.activeMenu('sitevisit'));

    // Load the CP, Sales-Manager and Project options for the "Filter by" dropdown.
    getAllCp('');
    getAllUser('');
    getAllProject('');

    const scope = resolveInitialScope();
    setUserIds(scope);

    // Resolve verifier / approver names for the modal, then build the
    // "Verification Pending With" options (approvers with pending visits).
    getUsersNameMap()
      .then((map) => {
        setUserNameMap(map);
        loadPendingApprovers(scope, map);
      })
      .catch((err) => console.log('getUsersNameMap error', err));

    getAllSitesFromServer(
      0,
      false,
      search,
      drpDownSelectedVal,
      selectedCp,
      selectedSales,
      selectDates,
      selectedDates,
      scope
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //*snapshot - refresh list on any SiteVisits update
  useEffect(() => {
    let updateListener = supabase
      .from('SiteVisits')
      .on('UPDATE', (handleRecordUpdated) => {
        if (handleRecordUpdated.eventType === 'UPDATE') {
          // Refetch with the CURRENT filters via the ref (this callback's own
          // closure is stale), so a verify/reject refresh keeps applied filters.
          if (refetchRef.current) refetchRef.current();
        }
      })
      .subscribe();
    return () => {
      if (updateListener) {
        supabase.removeSubscription(updateListener);
        updateListener = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIds]);

  // ---- "Filter by" dropdown: data loading + handlers (inlined from Leads) ----

  // Channel Partner options. Re-fetched on search; preserves currently-checked CPs.
  function getAllCp(searchVal) {
    getAllCpForLaunch(searchVal)
      .then((resp) => {
        const tempArray = resp.data.map((el) => ({
          label: el.billingName,
          value: false,
          id: el.cpId,
        }));
        const sortedCp = orderBy(
          tempArray,
          [(cp) => cp.label.toLowerCase()],
          ['asc']
        );
        setAllCpDropDown(
          sortedCp.map((cp) => ({ ...cp, value: selectedCp.includes(cp.id) }))
        );
      })
      .catch((err) => console.log(err));
  }

  // Sales-Manager (user) options. Non-Super-Admin users see only their own team.
  function getAllUser(searchVal) {
    if (!isSuperAdmin) {
      let team = [];
      try {
        team = JSON.parse(localStorage.getItem('teamIds')) || [];
      } catch (e) {
        team = [];
      }
      if (!Array.isArray(team) || team.length === 0) return;
      getUsers(searchVal, loggedInUser, team)
        .then((resp) => populateSales(resp))
        .catch((err) => console.log(err));
    } else {
      getUsers(searchVal, loggedInUser)
        .then((resp) => populateSales(resp))
        .catch((err) => console.log(err));
    }
  }

  function populateSales(resp) {
    const tempArray = resp.data.map((el) => ({
      label: el.name,
      value: false,
      id: el.userId,
    }));
    const sortedArray = orderBy(
      tempArray,
      [(u) => u.label.toLowerCase()],
      ['asc']
    );
    setAllSalesDropDown(
      sortedArray.map((u) => ({ ...u, value: selectedSales.includes(u.id) }))
    );
  }

  // Project options. Re-fetched on search; preserves currently-checked projects.
  function getAllProject(searchVal) {
    getProjectsForLeadFilter(loggedInUser?.name, loggedInUser?.userId, searchVal)
      .then((resp) => {
        const tempArray = (resp || []).map((el) => ({
          label: el.projectName,
          value: false,
          id: el.projectId,
        }));
        const sortedProjects = orderBy(
          tempArray,
          [(p) => p.label.toLowerCase()],
          ['asc']
        );
        setAllProjectsDropDown(
          sortedProjects.map((p) => ({
            ...p,
            value: selectedProjects.includes(p.id),
          }))
        );
      })
      .catch((err) => console.log(err));
  }

  const debouncedCpSearch = debounce((e) => {
    setDropDownCpSearch(e.target.value);
    getAllCp(e.target.value);
  }, 800);

  const debouncedSalesSearch = debounce((e) => {
    setDropDownSalesSearch(e.target.value);
    getAllUser(e.target.value);
  }, 800);

  const debouncedProjectSearch = debounce((e) => {
    setDropDownProjectSearch(e.target.value);
    getAllProject(e.target.value);
  }, 800);

  // "Verification Pending With" options: approvers (userParentId) that currently have
  // pending site visits, mapped to names via the userNameMap.
  function loadPendingApprovers(scope, nameMap) {
    getPendingApprovers(scope)
      .then((ids) => {
        const options = orderBy(
          ids.map((id) => ({ label: nameMap?.[id] || '-', value: false, id })),
          [(o) => o.label.toLowerCase()],
          ['asc']
        );
        setAllPendingWithDropDown(options);
      })
      .catch((err) => console.log('getPendingApprovers error', err));
  }

  // Opens one sub-panel inside the dropdown; "None" clears everything.
  function openPanel(label) {
    setShowAllCpDropDown(label === 'CP');
    setShowAllSalesDropDown(label === 'SM');
    setShowAllProjectsDropDown(label === 'Project');
    setShowAllLeadQualityDropDown(label === 'Lead Quality');
    setShowAllStatusDropDown(label === 'Status');
    setShowAllVerificationDropDown(label === 'Verification Status');
    setShowAllLeadTypeDropDown(label === 'Lead Type');
    setShowAllPendingWithDropDown(label === 'Verification Pending With');
    setShowDates(label === 'Date');
    // Restore the last typed search text into the panel's search box (mirrors Leads).
    if (label === 'CP') {
      setTimeout(() => {
        const el = document.getElementById(
          'input-with-icon-textfield DropDownSiteVisitCpSearch'
        );
        if (el) el.value = DropDownCpSearch;
      }, 100);
    }
    if (label === 'SM') {
      setTimeout(() => {
        const el = document.getElementById(
          'input-with-icon-textfield DropDownSiteVisitSalesSearch'
        );
        if (el) el.value = DropdownSalesSearch;
      }, 100);
    }
    if (label === 'Project') {
      setTimeout(() => {
        const el = document.getElementById(
          'input-with-icon-textfield DropDownSiteVisitProjectsSearch'
        );
        if (el) el.value = DropdownProjectSearch;
      }, 100);
    }
    if (label === 'None') handleClear();
  }

  function clearFilterState() {
    setSelectedCp([]);
    setSelectedSales([]);
    setSelectedProjects([]);
    setSelectedLeadQuality([]);
    setSelectedStatus([]);
    setSelectedVerification([]);
    setSelectedLeadType([]);
    setSelectedPendingWith([]);
    setSelectedDate(null);
    setSelectDates([
      { label: 'StartDate', value: '' },
      { label: 'EndDate', value: '' },
    ]);
    setAllCpDropDown((pre) => pre.map((m) => ({ ...m, value: false })));
    setAllSalesDropDown((pre) => pre.map((m) => ({ ...m, value: false })));
    setAllProjectsDropDown((pre) => pre.map((m) => ({ ...m, value: false })));
    setAllLeadQualityDropDown((pre) => pre.map((m) => ({ ...m, value: false })));
    setAllStatusDropDown((pre) => pre.map((m) => ({ ...m, value: false })));
    setAllVerificationDropDown((pre) => pre.map((m) => ({ ...m, value: false })));
    setAllLeadTypeDropDown((pre) => pre.map((m) => ({ ...m, value: false })));
    setAllPendingWithDropDown((pre) => pre.map((m) => ({ ...m, value: false })));
    setHelperText({ startDate: '', endDate: '' });
    setDropDownCpSearch('');
    setDropDownSalesSearch('');
    setDropDownProjectSearch('');
    setDropdownPendingWithSearch('');
    setShowDates(false);
    setShowAllCpDropDown(false);
    setShowAllSalesDropDown(false);
    setShowAllProjectsDropDown(false);
    setShowAllLeadQualityDropDown(false);
    setShowAllStatusDropDown(false);
    setShowAllVerificationDropDown(false);
    setShowAllLeadTypeDropDown(false);
    setShowAllPendingWithDropDown(false);
  }

  function handleClear() {
    clearFilterState();
    setShowDropDown(false);
    setPageNumber(0);
    setPageMaintain(0);
    setAllSitesData([]);
    const emptyDates = [
      { label: 'StartDate', value: '' },
      { label: 'EndDate', value: '' },
    ];
    getAllSitesFromServer(
      0,
      false,
      search,
      drpDownSelectedVal,
      [],
      [],
      emptyDates,
      null,
      userIds,
      [],
      [],
      [],
      [],
      [],
      []
    );
  }

  function handleApply() {
    // A start date with no end date is invalid.
    if (selectDates[0].value !== '' && selectDates[1].value === '') {
      setHelperText((pre) => ({ ...pre, endDate: 'Please Select end Date' }));
      setShowDates(true);
      return;
    }
    if (helperText.startDate !== '' || helperText.endDate !== '') {
      setShowDropDown(true);
      return;
    }
    const datesActive =
      selectDates[0].value !== '' && selectDates[1].value !== '';
    const appliedDates = datesActive ? 'notNull' : null;
    setSelectedDate(appliedDates);
    setShowDropDown(false);
    setPageNumber(0);
    setPageMaintain(0);
    setAllSitesData([]);
    getAllSitesFromServer(
      0,
      false,
      search,
      drpDownSelectedVal,
      selectedCp,
      selectedSales,
      selectDates,
      appliedDates,
      userIds,
      selectedProjects,
      selectedLeadQuality,
      selectedStatus,
      selectedVerification,
      selectedLeadType,
      selectedPendingWith
    );
  }

  function toggleCp(each, index, isChecked) {
    setAllCpDropDown((pre) => {
      const next = [...pre];
      next[index] = { ...next[index], value: isChecked };
      return next;
    });
    setSelectedCp((pre) =>
      isChecked ? [...pre, each.id] : pre.filter((id) => id !== each.id)
    );
  }

  function toggleSales(each, index, isChecked) {
    setAllSalesDropDown((pre) => {
      const next = [...pre];
      next[index] = { ...next[index], value: isChecked };
      return next;
    });
    setSelectedSales((pre) =>
      isChecked ? [...pre, each.id] : pre.filter((id) => id !== each.id)
    );
  }

  function toggleProject(each, index, isChecked) {
    setAllProjectsDropDown((pre) => {
      const next = [...pre];
      next[index] = { ...next[index], value: isChecked };
      return next;
    });
    setSelectedProjects((pre) =>
      isChecked ? [...pre, each.id] : pre.filter((id) => id !== each.id)
    );
  }

  // Lead Quality + Status filter on the label value (no id).
  function toggleLeadQuality(each, index, isChecked) {
    setAllLeadQualityDropDown((pre) => {
      const next = [...pre];
      next[index] = { ...next[index], value: isChecked };
      return next;
    });
    setSelectedLeadQuality((pre) =>
      isChecked ? [...pre, each.label] : pre.filter((v) => v !== each.label)
    );
  }

  function toggleStatus(each, index, isChecked) {
    setAllStatusDropDown((pre) => {
      const next = [...pre];
      next[index] = { ...next[index], value: isChecked };
      return next;
    });
    setSelectedStatus((pre) =>
      isChecked ? [...pre, each.label] : pre.filter((v) => v !== each.label)
    );
  }

  // Verification filter maps the label to a siteVisitVerificationStatus value:
  // 'Verified' -> 'approved', 'Rejected' -> 'rejected', 'Verification Pending' -> 'pending'.
  function toggleVerification(each, index, isChecked) {
    const statusVal =
      each.label === 'Verified'
        ? 'approved'
        : each.label === 'Rejected'
        ? 'rejected'
        : 'pending';
    setAllVerificationDropDown((pre) => {
      const next = [...pre];
      next[index] = { ...next[index], value: isChecked };
      return next;
    });
    setSelectedVerification((pre) =>
      isChecked ? [...pre, statusVal] : pre.filter((v) => v !== statusVal)
    );
  }

  // Lead Type filters on the label value (Direct / CP / R&L).
  function toggleLeadType(each, index, isChecked) {
    setAllLeadTypeDropDown((pre) => {
      const next = [...pre];
      next[index] = { ...next[index], value: isChecked };
      return next;
    });
    setSelectedLeadType((pre) =>
      isChecked ? [...pre, each.label] : pre.filter((v) => v !== each.label)
    );
  }

  // "Verification Pending With" filters on the approver's userId (userParentId).
  // Matches by id (not index) so the client-side search filter can't misalign it.
  function togglePendingWith(each, isChecked) {
    setAllPendingWithDropDown((pre) =>
      pre.map((m) => (m.id === each.id ? { ...m, value: isChecked } : m))
    );
    setSelectedPendingWith((pre) =>
      isChecked ? [...pre, each.id] : pre.filter((id) => id !== each.id)
    );
  }

  // Called from inside the View Details modal (after the confirmation popup).
  // `comment` is the mandatory verification note entered in the modal.
  function handleVerify(row, comment) {
    updateSiteVisitVerification(row.sitevisitid, loggedInUser.userId, Date.now(), comment)
      .then(() => {
        addLeadLogs({
          leadId: row.leadid,
          userId: row.userid,
          cpId: row.cpid,
          fosId: row.fosid,
          leadStatus: 'Site Visit Verified',
          message: `Site Visit Verified by ${loggedInUser?.name}`,
        }).catch((err) => console.log('lead log error', err));

        successTost('Site visit verified');
        handleViewDetailsClose();
        // Refresh the listing so the status chip reflects the new state.
        getAllSitesFromServer(
          pageNumber,
          false,
          search,
          drpDownSelectedVal,
          selectedCp,
          selectedSales,
          selectDates,
          selectedDates,
          userIds
        );
      })
      .catch((err) => {
        console.log('verify error', err);
        errorTost('Could not verify the site visit. Please try again.');
      });
  }

  // Rejects a site visit (sets siteVisitVerificationStatus = 'rejected', records the
  // rejecter in verified_by, stores the reason in comment). `comment` from the modal.
  // Eligibility is the same as verify and is gated in the modal via the canApprove prop.
  function handleReject(row, comment) {
    updateSiteVisitRejection(row.sitevisitid, loggedInUser.userId, Date.now(), comment)
      .then(() => {
        // Record a lead-log entry for the rejection (mirrors the verify flow):
        // status + who rejected, with the site visit's own associations.
        addLeadLogs({
          leadId: row.leadid,
          userId: row.userid,
          cpId: row.cpid,
          fosId: row.fosid,
          leadStatus: 'Site Visit Rejected',
          message: `Site Visit Rejected by ${loggedInUser?.name}`,
        }).catch((err) => console.log('lead log error', err));

        successTost('Site visit rejected');
        handleViewDetailsClose();
        getAllSitesFromServer(
          pageNumber,
          false,
          search,
          drpDownSelectedVal,
          selectedCp,
          selectedSales,
          selectDates,
          selectedDates,
          userIds
        );
      })
      .catch((err) => {
        console.log('reject error', err);
        errorTost('Could not reject the site visit. Please try again.');
      });
  }

  const CpColumns = [
    {
      field: 'Site',
      headerName: 'Leads',
      flex: 2.2,
      minWidth: 260,
      renderCell: (params) => {
        return (
          <Box className={cssClasses.SubContainer}>
            {params.row.sitevisiturl ? (
              <img
                style={{
                  width: '4rem',
                  height: '4rem',
                  objectFit: 'cover',
                  borderRadius: '50%',
                }}
                src={
                  process.env.REACT_APP_SUPABASE_BUCKET_PREFIX +
                  params.row.sitevisiturl
                }
                alt={''}
              />
            ) : (
              <img src={ProjectIcon} alt={''} />
            )}

            <Box>
              <Box className={cssClasses.BoldTitle} whiteSpace={'break-spaces'}>
                {params?.row?.leadname ? params?.row?.leadname : '-'}
              </Box>
              <Box>
                <Box
                  component={'span'}
                  className={cssClasses.semiBoldTitle}
                  whiteSpace={'break-spaces'}
                >
                  VJ
                  {params?.row?.leaddisplayid
                    ? params?.row?.leaddisplayid?.replace(
                        params?.row?.leaddisplayid?.substring(8, 12),
                        '-' + params?.row?.leaddisplayid?.substring(8, 12)
                      )
                    : '-'}
                </Box>
              </Box>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'AllotmentStatus',
      headerName: 'Allotment Status',
      flex: 1.2,
      minWidth: 170,
      renderCell: (params) => {
        return (
          <Box className={cssClasses.SubContainer}>
            <Box className={cssClasses.BoldTitle} whiteSpace={'break-spaces'}>
              {params?.row?.allotmentstatus ? params?.row?.allotmentstatus : '-'}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'Sales',
      headerName: 'Sales Representative',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => {
        return (
          <Box className={cssClasses.SubContainer}>
            <Box className={cssClasses.BoldTitle} whiteSpace={'break-spaces'}>
              {params?.row?.username ? params?.row?.username : '-'}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'VisitDateTime',
      headerName: 'Visit Date & Time ',
      flex: 1.4,
      minWidth: 200,
      renderCell: (params) => {
        return (
          <Box whiteSpace={'break-spaces'}>
            {new Date(params.row.sitevisitdate).toDateString()},{' '}
            {new Date(params.row.sitevisitdate).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        // Three states from siteVisitVerificationStatus ('approved' | 'rejected' | 'pending').
        const isVerified = params.row.sitevisitverificationstatus === 'approved';
        const isRejected = params.row.sitevisitverificationstatus === 'rejected';
        const palette = isVerified
          ? { bg: '#ECFDF3', color: '#067647', border: '#ABEFC6', dot: '#17B26A', label: 'Verified' }
          : isRejected
          ? { bg: '#FEF3F2', color: '#B42318', border: '#FECDCA', dot: '#F04438', label: 'Rejected' }
          : { bg: '#FFFAEB', color: '#B54708', border: '#FEDF89', dot: '#F79009', label: 'Verification Pending' };
        return (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.6rem',
              padding: '0.3rem 1.2rem',
              borderRadius: '1.6rem',
              fontSize: '1.3rem',
              fontFamily: 'Inter-Medium',
              backgroundColor: palette.bg,
              color: palette.color,
              border: `1px solid ${palette.border}`,
            }}
          >
            <Box
              component={'span'}
              sx={{
                width: '0.7rem',
                height: '0.7rem',
                borderRadius: '50%',
                backgroundColor: palette.dot,
              }}
            />
            {palette.label}
          </Box>
        );
      },
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => {
        return (
          <Button
            style={{
              color: '#155EEF',
              backgroundColor: '#FFF',
              border: '1px solid #155EEF',
              fontSize: '1.4rem',
              lineHeight: '2rem',
            }}
            btnName={'View Details'}
            padding={'0.5rem 2.5rem'}
            clicked={() => {
              setViewSiteData({
                ...params.row,
                // Verifier name (when verified) and designated approver name (lead's userParentId).
                verifiedbyname: userNameMap[params.row.verified_by] || '',
                userparentname: userNameMap[params.row.userparentid] || '',
              });
              handleViewDetailsOpen();
            }}
          />
        );
      },
    },
  ];

  const debouncedFetchSearchData = debounce((e) => {
    // Searching always starts from the first page (otherwise a stale page index
    // could request an out-of-range slice of the filtered results and show nothing).
    setSearch(e.target.value);
    setPageNumber(0);
    setPageMaintain(0);
    getAllSitesFromServer(
      0,
      false,
      e.target.value,
      drpDownSelectedVal,
      selectedCp,
      selectedSales,
      selectDates,
      selectedDates,
      userIds
    );
  }, 800);

  function getAllSitesFromServer(
    pageNumber,
    merge,
    search,
    drpDownSelectedVal,
    selectedCp,
    selectedSales,
    selectDates,
    selectedDates,
    passedUserIds,
    projectsArg,
    qualityArg,
    statusArg,
    verificationArg,
    leadTypeArg,
    pendingWithArg
  ) {
    const idsToUse = passedUserIds !== undefined ? passedUserIds : userIds;
    // The Project / Lead Quality / Status / Verification / Lead Type / Pending With
    // selections default to current state, so existing callers (mount, search,
    // pagination, snapshot) don't need to pass them; only Apply / Clear pass explicit values.
    const projects = projectsArg !== undefined ? projectsArg : selectedProjects;
    const quality = qualityArg !== undefined ? qualityArg : selectedLeadQuality;
    const statuses = statusArg !== undefined ? statusArg : selectedStatus;
    const verification =
      verificationArg !== undefined ? verificationArg : selectedVerification;
    const leadTypes = leadTypeArg !== undefined ? leadTypeArg : selectedLeadType;
    const pendingWith =
      pendingWithArg !== undefined ? pendingWithArg : selectedPendingWith;
    // null = "All" (no hierarchy filter) -> proceed. undefined = not resolved, or an
    // empty array = nothing in scope -> bail and show the empty state.
    if (idsToUse === undefined || (Array.isArray(idsToUse) && idsToUse.length === 0)) {
      setAllSitesData([]);
      setTotalSiteVisits(0);
      setNoSiteVisitPresent(null);
      return;
    }

    getAllSiteVisitsByHierarchy(
      idsToUse,
      pageNumber * pageSize,
      pageNumber * pageSize + pageSize - 1,
      search,
      drpDownSelectedVal,
      selectedCp,
      selectedSales,
      selectDates,
      selectedDates,
      projects,
      quality,
      statuses,
      verification,
      leadTypes,
      pendingWith
    )
      .then((resp) => {
        setTotalSiteVisits(resp.count);
        setNoSiteVisitPresent(resp.count > 0);
        const SiteVisitArr = resp.data.map((el, index) => ({
          ...el,
          id: el.sitevisitid,
          index: index,
          sitevisitdate: el.sitevisitdate,
        }));

        if (merge) {
          setAllSitesData((pre) => [...pre, ...SiteVisitArr]);
        } else {
          setAllSitesData([...SiteVisitArr]);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }

  function getMoreData(passPageNumber) {
    setPageNumber(passPageNumber);
    getAllSitesFromServer(
      passPageNumber,
      true,
      search,
      drpDownSelectedVal,
      selectedCp,
      selectedSales,
      selectDates,
      selectedDates,
      userIds
    );
  }

  // Keep the ref pointing at a refetch that uses the CURRENT filters/search/scope,
  // so the real-time subscription's refresh (after verify/reject) preserves filters.
  refetchRef.current = () => {
    getAllSitesFromServer(
      0,
      false,
      search,
      drpDownSelectedVal,
      selectedCp,
      selectedSales,
      selectDates,
      selectedDates,
      userIds,
      selectedProjects,
      selectedLeadQuality,
      selectedStatus,
      selectedVerification,
      selectedLeadType,
      selectedPendingWith
    );
  };

  // Labels shown in the collapsed "Filter by" header / left-list checkmarks.
  const activeFilterLabels = [];
  if (selectedDates) activeFilterLabels.push('Date');
  if (selectedCp.length) activeFilterLabels.push('CP');
  if (selectedSales.length) activeFilterLabels.push('SM');
  if (selectedProjects.length) activeFilterLabels.push('Project');
  // Lead Quality & Status temporarily hidden from the dropdown (see filterCategories).
  // if (selectedLeadQuality.length) activeFilterLabels.push('Lead Quality');
  // if (selectedStatus.length) activeFilterLabels.push('Status');
  if (selectedVerification.length) activeFilterLabels.push('Verification Status');
  if (selectedLeadType.length) activeFilterLabels.push('Lead Type');
  if (selectedPendingWith.length) activeFilterLabels.push('Verification Pending With');

  return (
    <div className='pageWrapper'>
      <div className={cssClasses.wrapperHeader}>
        <Box>
          <Box className={cssClasses.HeaderTitle}>Site Visit Approval</Box>
          <Box className={cssClasses.SubTitle}>
            {isSuperAdmin || seeAll
              ? 'Showing all site visits'
              : 'Site visits across your team'}
            <Box
              component={'span'}Sales Representative
              sx={{ color: '#98A2B3', fontWeight: 400, marginLeft: '0.5rem' }}
            >
              · FY 2026-27 (Apr 2026 – Mar 2027)
            </Box>
          </Box>
        </Box>
      </div>
      <Divider />

      <div className={cssClasses.wrapperFilters}>
        <div className={cssClasses.containerFilterSortBy}>
          <Box className={cssClasses.countTitle} mt={3}>
            {TotalSiteVisits} Site Visits
          </Box>

          {/* "Filter by" multi-select (SM + CP + Date) — inlined from the Leads tab. */}
          <div className={filterCss.wrapperFilter}>
            <div className={filterCss.filterTitle}>Filter by</div>
            <div className={filterCss.wrapperDropDown}>
              <div
                className={filterCss.wrapperCustomiseDropDown}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDropDown(!showDropDown);
                }}
              >
                <div className={filterCss.dropDownLabel}>
                  <span className={filterCss.dropDownLabelValue}>
                    {activeFilterLabels.join()}
                  </span>
                  {activeFilterLabels.length === 0 && (
                    <span className={filterCss.dropDownLabelValue}>None</span>
                  )}
                </div>
                {showDropDown ? (
                  <KeyboardArrowUpIcon className={filterCss.dropDownIcon} />
                ) : (
                  <KeyboardArrowDownIcon className={filterCss.dropDownIcon} />
                )}
              </div>

              {showDropDown && (
                <ClickAwayListener onClickAway={() => setShowDropDown(false)}>
                  <div className={filterCss.containerCustomiseDropDown}>
                    <div className={filterCss.wrapperSelectAndCheckbox}>
                      <div className={filterCss.wrapperEachSelect}>
                        {filterCategories.map((label, index) => {
                          const isSelected =
                            label === 'None'
                              ? activeFilterLabels.length === 0
                              : activeFilterLabels.includes(label);
                          return (
                            <div
                              key={index}
                              className={
                                isSelected
                                  ? filterCss.selectedVal
                                  : filterCss.containerEachSelect
                              }
                            >
                              <div
                                className={filterCss.eachSelect}
                                onClick={() => openPanel(label)}
                              >
                                {label}
                              </div>
                              {isSelected && (
                                <img
                                  src={checked}
                                  alt={'checked'}
                                  style={{ marginRight: '2rem' }}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>

                      <div className={filterCss.wrapperCheckbox1}>
                        {showAllCpDropDown && (
                          <Box width={'100%'} mb={2}>
                            <SearchBar
                              id={'DropDownSiteVisitCpSearch'}
                              placeholder={'Search'}
                              search={debouncedCpSearch}
                              width='100%'
                            />
                          </Box>
                        )}
                        {showAllSalesDropDown && (
                          <Box width={'100%'} mb={2}>
                            <SearchBar
                              id={'DropDownSiteVisitSalesSearch'}
                              placeholder={'Search'}
                              search={debouncedSalesSearch}
                              width='100%'
                            />
                          </Box>
                        )}
                        {showAllProjectsDropDown && (
                          <Box width={'100%'} mb={2}>
                            <SearchBar
                              id={'DropDownSiteVisitProjectsSearch'}
                              placeholder={'Search'}
                              search={debouncedProjectSearch}
                              width='100%'
                            />
                          </Box>
                        )}
                        {showAllPendingWithDropDown && (
                          <Box width={'100%'} mb={2}>
                            <SearchBar
                              id={'DropDownSiteVisitPendingWithSearch'}
                              placeholder={'Search'}
                              search={(e) =>
                                setDropdownPendingWithSearch(e.target.value)
                              }
                              width='100%'
                            />
                          </Box>
                        )}

                        <div className={filterCss.wrapperCheckbox}>
                          {showAllCpDropDown &&
                            (allCpDropDown?.length > 0 ? (
                              allCpDropDown.map((each, index) => (
                                <FormGroup key={each.id}>
                                  <FormControlLabel
                                    sx={[
                                      {
                                        '& .MuiTypography-root': {
                                          fontFamily: 'Inter-Medium !important',
                                          fontStyle: 'normal',
                                          fontWeight: '400',
                                          fontSize: '1.6rem !important',
                                          lineHeight: '2.4rem !important',
                                          color: '#101828',
                                        },
                                      },
                                    ]}
                                    checked={each.value}
                                    onChange={(e) =>
                                      toggleCp(each, index, e.target.checked)
                                    }
                                    control={
                                      <Checkbox
                                        sx={{
                                          '& .MuiSvgIcon-root': {
                                            fontSize: 28,
                                          },
                                        }}
                                      />
                                    }
                                    label={each.label}
                                  />
                                </FormGroup>
                              ))
                            ) : (
                              <div className={filterCss.label}>No CP Found</div>
                            ))}

                          {showAllSalesDropDown &&
                            (allSalesDropDown?.length > 0 ? (
                              allSalesDropDown.map((each, index) => (
                                <FormGroup key={each.id}>
                                  <FormControlLabel
                                    sx={[
                                      {
                                        '& .MuiTypography-root': {
                                          fontFamily: 'Inter-Medium !important',
                                          fontStyle: 'normal',
                                          fontWeight: '400',
                                          fontSize: '1.6rem !important',
                                          lineHeight: '2.4rem !important',
                                          color: '#101828',
                                        },
                                      },
                                    ]}
                                    checked={each.value}
                                    onChange={(e) =>
                                      toggleSales(each, index, e.target.checked)
                                    }
                                    control={
                                      <Checkbox
                                        sx={{
                                          '& .MuiSvgIcon-root': {
                                            fontSize: 28,
                                          },
                                        }}
                                      />
                                    }
                                    label={each.label}
                                  />
                                </FormGroup>
                              ))
                            ) : (
                              <div className={filterCss.label}>
                                No SM User Found
                              </div>
                            ))}

                          {showAllProjectsDropDown &&
                            (allProjectsDropDown?.length > 0 ? (
                              allProjectsDropDown.map((each, index) => (
                                <FormGroup key={each.id}>
                                  <FormControlLabel
                                    sx={[
                                      {
                                        '& .MuiTypography-root': {
                                          fontFamily: 'Inter-Medium !important',
                                          fontStyle: 'normal',
                                          fontWeight: '400',
                                          fontSize: '1.6rem !important',
                                          lineHeight: '2.4rem !important',
                                          color: '#101828',
                                        },
                                      },
                                    ]}
                                    checked={each.value}
                                    onChange={(e) =>
                                      toggleProject(each, index, e.target.checked)
                                    }
                                    control={
                                      <Checkbox
                                        sx={{
                                          '& .MuiSvgIcon-root': {
                                            fontSize: 28,
                                          },
                                        }}
                                      />
                                    }
                                    label={each.label}
                                  />
                                </FormGroup>
                              ))
                            ) : (
                              <div className={filterCss.label}>
                                No Project Found
                              </div>
                            ))}

                          {showAllLeadQualityDropDown &&
                            allLeadQualityDropDown.map((each, index) => (
                              <FormGroup key={each.label}>
                                <FormControlLabel
                                  sx={[
                                    {
                                      '& .MuiTypography-root': {
                                        fontFamily: 'Inter-Medium !important',
                                        fontStyle: 'normal',
                                        fontWeight: '400',
                                        fontSize: '1.6rem !important',
                                        lineHeight: '2.4rem !important',
                                        color: '#101828',
                                      },
                                    },
                                  ]}
                                  checked={each.value}
                                  onChange={(e) =>
                                    toggleLeadQuality(each, index, e.target.checked)
                                  }
                                  control={
                                    <Checkbox
                                      sx={{
                                        '& .MuiSvgIcon-root': { fontSize: 28 },
                                      }}
                                    />
                                  }
                                  label={each.label}
                                />
                              </FormGroup>
                            ))}

                          {showAllStatusDropDown &&
                            allStatusDropDown.map((each, index) => (
                              <FormGroup key={each.label}>
                                <FormControlLabel
                                  sx={[
                                    {
                                      '& .MuiTypography-root': {
                                        fontFamily: 'Inter-Medium !important',
                                        fontStyle: 'normal',
                                        fontWeight: '400',
                                        fontSize: '1.6rem !important',
                                        lineHeight: '2.4rem !important',
                                        color: '#101828',
                                      },
                                    },
                                  ]}
                                  checked={each.value}
                                  onChange={(e) =>
                                    toggleStatus(each, index, e.target.checked)
                                  }
                                  control={
                                    <Checkbox
                                      sx={{
                                        '& .MuiSvgIcon-root': { fontSize: 28 },
                                      }}
                                    />
                                  }
                                  label={each.label}
                                />
                              </FormGroup>
                            ))}

                          {showAllVerificationDropDown &&
                            allVerificationDropDown.map((each, index) => (
                              <FormGroup key={each.label}>
                                <FormControlLabel
                                  sx={[
                                    {
                                      '& .MuiTypography-root': {
                                        fontFamily: 'Inter-Medium !important',
                                        fontStyle: 'normal',
                                        fontWeight: '400',
                                        fontSize: '1.6rem !important',
                                        lineHeight: '2.4rem !important',
                                        color: '#101828',
                                      },
                                    },
                                  ]}
                                  checked={each.value}
                                  onChange={(e) =>
                                    toggleVerification(each, index, e.target.checked)
                                  }
                                  control={
                                    <Checkbox
                                      sx={{
                                        '& .MuiSvgIcon-root': { fontSize: 28 },
                                      }}
                                    />
                                  }
                                  label={each.label}
                                />
                              </FormGroup>
                            ))}

                          {showAllLeadTypeDropDown &&
                            allLeadTypeDropDown.map((each, index) => (
                              <FormGroup key={each.label}>
                                <FormControlLabel
                                  sx={[
                                    {
                                      '& .MuiTypography-root': {
                                        fontFamily: 'Inter-Medium !important',
                                        fontStyle: 'normal',
                                        fontWeight: '400',
                                        fontSize: '1.6rem !important',
                                        lineHeight: '2.4rem !important',
                                        color: '#101828',
                                      },
                                    },
                                  ]}
                                  checked={each.value}
                                  onChange={(e) =>
                                    toggleLeadType(each, index, e.target.checked)
                                  }
                                  control={
                                    <Checkbox
                                      sx={{
                                        '& .MuiSvgIcon-root': { fontSize: 28 },
                                      }}
                                    />
                                  }
                                  label={each.label}
                                />
                              </FormGroup>
                            ))}

                          {showAllPendingWithDropDown &&
                            (() => {
                              const filtered = allPendingWithDropDown.filter((o) =>
                                (o.label || '')
                                  .toLowerCase()
                                  .includes(DropdownPendingWithSearch.toLowerCase())
                              );
                              if (filtered.length === 0) {
                                return (
                                  <div className={filterCss.label}>
                                    No pending approvers
                                  </div>
                                );
                              }
                              return filtered.map((each) => (
                                <FormGroup key={each.id}>
                                  <FormControlLabel
                                    sx={[
                                      {
                                        '& .MuiTypography-root': {
                                          fontFamily: 'Inter-Medium !important',
                                          fontStyle: 'normal',
                                          fontWeight: '400',
                                          fontSize: '1.6rem !important',
                                          lineHeight: '2.4rem !important',
                                          color: '#101828',
                                        },
                                      },
                                    ]}
                                    checked={each.value}
                                    onChange={(e) =>
                                      togglePendingWith(each, e.target.checked)
                                    }
                                    control={
                                      <Checkbox
                                        sx={{
                                          '& .MuiSvgIcon-root': { fontSize: 28 },
                                        }}
                                      />
                                    }
                                    label={each.label}
                                  />
                                </FormGroup>
                              ));
                            })()}

                          {showDates && (
                            <div>
                              <div className={filterCss.dateDivider}>
                                <div className={filterCss.label}>
                                  Start Date
                                </div>
                                <DateComponent
                                  value={selectDates[0].value}
                                  minDate={'1 Jan'}
                                  onChangeDate={(value) => {
                                    setSelectedDate('notNull');
                                    setSelectDates((pre) => {
                                      const next = [...pre];
                                      next[0] = { ...next[0], value };
                                      return next;
                                    });
                                    setHelperText((pre) => ({
                                      ...pre,
                                      endDate: '',
                                    }));
                                  }}
                                  helperText={helperText.startDate}
                                ></DateComponent>
                              </div>
                              <div>
                                <div className={filterCss.label}>End Date</div>
                                <DateComponent
                                  value={selectDates[1].value}
                                  minDate={'1 Jan'}
                                  onChangeDate={(value) => {
                                    setSelectedDate('notNull');
                                    if (selectDates[0].value === '') {
                                      setHelperText((pre) => ({
                                        ...pre,
                                        endDate:
                                          'Please select start date first',
                                      }));
                                    } else if (selectDates[0].value > value) {
                                      setHelperText((pre) => ({
                                        ...pre,
                                        endDate:
                                          'Select End Date Greater than start Date',
                                      }));
                                    } else {
                                      setSelectDates((pre) => {
                                        const next = [...pre];
                                        next[1] = { ...next[1], value };
                                        return next;
                                      });
                                      setHelperText((pre) => ({
                                        ...pre,
                                        endDate: '',
                                      }));
                                    }
                                  }}
                                  helperText={helperText.endDate}
                                ></DateComponent>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className={filterCss.wrapperDropDownButtons}>
                          <div>
                            <Button
                              style={{
                                color: '#344054',
                                backgroundColor: '#ffffff',
                                fontSize: '1.4rem',
                                lineHeight: '2rem',
                              }}
                              padding={'0.8rem'}
                              btnName={'Clear'}
                              clicked={handleClear}
                            />
                          </div>
                          <div>
                            <Button
                              style={{
                                color: '#ffffff',
                                backgroundColor: '#1570EF',
                                fontSize: '1.4rem',
                                lineHeight: '2rem',
                              }}
                              padding={'0.8rem'}
                              btnName={'Apply'}
                              clicked={handleApply}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </ClickAwayListener>
              )}
            </div>
          </div>
        </div>
        <div className={cssClasses.wrapperSearchBar}>
          <SearchBar
            id={'StudentSearch'}
            value={search}
            placeholder={'Search By LeadName'}
            search={debouncedFetchSearchData}
            width='100%'
          />
        </div>
      </div>

      {noSiteVisitPresent === true && (
        <Box className={cssClasses.GridWrapper}>
          <DataGrid
            rows={AllSitesData}
            columns={CpColumns}
            pageSize={10}
            rowsPerPageOptions={[10]}
            page={pageMaintain}
            onPageChange={(_page) => {
              setPageMaintain(_page);
              if (pageNumber < _page) {
                getMoreData(_page);
              }
            }}
            sx={[dataGridStyles]}
          />
        </Box>
      )}

      {noSiteVisitPresent === false && (
        <Box>
          <Box width={'100%'} height={'100%'}>
            <Box className={cssClasses.noDataMsg}>
              <Box className={cssClasses.emptyIcon}>
                <img src={NoSiteVisitIcon} alt='No Site Visits Yet' />
              </Box>
              <Box className={cssClasses.emptyTitle}>
                No Site visit records were found
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {openViewDetails && (
        <ViewSiteVisitDetails
          open={openViewDetails}
          handleClose={handleViewDetailsClose}
          data={ViewSiteData}
          enableZoom={true}
          showVerification={true}
          canApprove={
            canApprove &&
            (isSuperAdmin || teamIds.includes(ViewSiteData?.userparentid))
          }
          onVerify={handleVerify}
          onReject={handleReject}
        />
      )}
    </div>
  );
}

export default SiteVisit;
